import {
  Component,
  input,
  OnInit,
  DestroyRef,
  inject,
  signal,
  ViewChild,
  output,
  computed,
  forwardRef,
  effect,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSplitterModule } from 'ng-zorro-antd/splitter';

import { AuthStore } from '@rapider/authentication';
import {
  NotificationService,
  BrowserStorageManagementService,
} from '@rapider/services';
import {
  ProjectDataService,
  ProjectDataActions,
  ModelMutationDescriptor,
  selectProjectModelsList,
  extractRapiderApiEntitiesFromHtml,
  matchPageEntitiesWithMutations,
  clearSwrSessionCacheForEntities,
} from '@rapider/project-data';
import { environment } from '@rapider/environments';
import { DefinitionControllerService } from '@rapider/sdk';

import { HtmlPreviewComponent } from '../html-preview/html-preview.component';
import { DynamicPageRendererComponent } from '../dynamic-page-renderer/dynamic-page-renderer.component';
import { PageConfig } from '../dynamic-page-renderer/dynamic-page-renderer.config';
import {
  isAllowedManifestEntity,
  isTrustedAppResourceUrl,
} from '../security/sandbox-security.util';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

/**
 * @class HtmlUrlPreviewComponent
 * @description A wrapper component that fetches raw HTML/JS content from a backend definition file
 * and passes it to the sandbox previewer. It provides hooks to listen to and send messages
 * into the executing iframe for future API data bindings, and orchestrates UI features like modals and splitters.
 */
@Component({
  selector: 'rapider-html-url-preview',
  standalone: true,
  imports: [
    CommonModule,
    HtmlPreviewComponent,
    NzModalModule,
    NzDrawerModule,
    NzSplitterModule,
    forwardRef(() => DynamicPageRendererComponent),
    // CRITICAL FIX: Removed self-import `forwardRef(() => HtmlUrlPreviewComponent)`.
    // Standalone components inherently possess self-reference inside their own templates.
    // Injecting them into the imports array causes the template factory to crash and render blank content.
  ],
  templateUrl: './html-url-preview.component.html',
  host: { class: 'w-full h-full flex-1 min-h-0 flex flex-col' },
})
export class HtmlUrlPreviewComponent implements OnInit {
  private projectDataService = inject(ProjectDataService);
  private authStore = inject(AuthStore);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly browserStorage = inject(BrowserStorageManagementService);
  private readonly definitionController = inject(DefinitionControllerService);

  private contentSub?: Subscription;
  private readonly usedEntitiesOnPage = new Set<string>();
  private readonly workspaceModels = this.store.selectSignal(
    selectProjectModelsList,
  );
  private static readonly htmlCache = new Map<string, string>();

  /** Clears cached CDN page HTML (e.g. after recompiling a develop build). */
  public static clearCache(): void {
    HtmlUrlPreviewComponent.htmlCache.clear();
  }

  /** Reference to the child previewer to enable direct messaging into the iframe. */
  @ViewChild(HtmlPreviewComponent)
  private previewComponent!: HtmlPreviewComponent;

  /** Input: The direct URL to the HTML file on the CDN. */
  public htmlUrl = input.required<string>();

  /** Optional Input: The name of the application for display purposes. */
  public appName = input<string>();

  /** Optional Input: The base URL for resolving relative assets (like /assets/) inside the HTML file. */
  public assetBaseUrl = input<string>('');

  /** Optional Input: Active semantic application theme key (e.g. 'default', 'obsidian-compact'). */
  public appTheme = input<string>('default');

  /** Optional Input: The array of pages from the app's manifest (for opening modals/drawers). */
  public pagesManifest = input<any[]>([]);

  /** Optional Input: Routing parameters passed down from the parent viewer. */
  public routeParams = input<Record<string, string>>({});

  /** Optional Input: The data models for the application to resolve API requests. */
  public dataModels = input<any[]>([]);

  /** Optional Input: Live code from an editor to bypass fetching. */
  public liveCode = input<string | undefined>(undefined);

  // Track if we should route rapiderApi to legacy Project APIs or new Data Manager
  public appDataModels = signal<any[]>([]);

  /** Input: Passes down the editor mode flag to inject the native trojan-horse builder scripts. */
  public enableEditorSdk = input<boolean>(false);

  /**
   * @property {InputSignal<boolean>} displayBrowserHeader
   * @description Optional configuration input controlling whether the mock browser container toolbar frame is visible.
   * When set to false, it is passed down to hide the visual wrapper frame completely. Defaults to true.
   */
  public displayBrowserHeader = input<boolean>(true);

  /** Optional Input: Test mode flag to intercept mutations without modifying real database data. */
  public testMode = input<boolean>(false);

  /** Optional Input: Enables allow-same-origin ONLY when parent developer tools (Builder, Bug Pinning, E2E Runner) need DOM inspection. */
  public allowSameOrigin = input<boolean>(false);

  /** Computed: Effective allowSameOrigin flag passed down to the sandbox iframe. */
  public effectiveAllowSameOrigin = computed(
    () => this.allowSameOrigin() || this.testMode() || this.enableEditorSdk(),
  );

  /** Output: Emits API lifecycle events (request, response, error) for external debuggers. */
  public onApiEvent = output<any>();

  /** Output: Emits Editor events (clicks, hovers, drags) captured by the internal SDK. */
  public onEditorEvent = output<any>();

  /** Output: Exposes the internal iframe element once loaded for drag-and-drop event binding. */
  public onIframeLoad = output<HTMLIFrameElement>();

  // State Signals
  public htmlContent = signal<string>('');
  public fetchedDefinitionName = signal<string | null>(null);
  public isLoading = signal<boolean>(true);
  public error = signal<string | null>(null);

  // --- PAGE MODAL STATE ---
  public isPageModalVisible = signal<boolean>(false);
  public isPageModalLoading = signal<boolean>(false);
  public pageModalConfig = signal<PageConfig | null>(null);
  public pageModalType = signal<string | null>(null);
  public pageModalId = signal<string | null>(null);
  public pageModalUrl = signal<string | null>(null);
  public pageModalPayload = signal<Record<string, any>>({});
  public pageModalTitle = signal<string>('Modal');
  public pageModalWidth = signal<number | string>(800);

  // --- PAGE DRAWER STATE ---
  public isPageDrawerVisible = signal<boolean>(false);
  public isPageDrawerLoading = signal<boolean>(false);
  public pageDrawerConfig = signal<PageConfig | null>(null);
  public pageDrawerType = signal<string | null>(null);
  public pageDrawerId = signal<string | null>(null);
  public pageDrawerUrl = signal<string | null>(null);
  public pageDrawerPayload = signal<Record<string, any>>({});
  public pageDrawerTitle = signal<string>('Drawer');
  public pageDrawerPlacement = signal<'left' | 'right' | 'top' | 'bottom'>(
    'right',
  );
  public pageDrawerSize = signal<'default' | 'large'>('large');
  public pageDrawerWidth = signal<number | string | undefined>(undefined);

  // --- PAGE SPLITTER STATE ---
  public isPageSplitterVisible = signal<boolean>(false);
  public isPageSplitterLoading = signal<boolean>(false);
  public pageSplitterConfig = signal<PageConfig | null>(null);
  public pageSplitterType = signal<string | null>(null);
  public pageSplitterId = signal<string | null>(null);
  public pageSplitterUrl = signal<string | null>(null);
  public pageSplitterPayload = signal<Record<string, any>>({});
  public pageSplitterTitle = signal<string | undefined>(undefined);
  public pageSplitterIcon = signal<string | undefined>(undefined);
  public pageSplitterDefaultSize = signal<number | string>('30%');
  public pageSplitterMin = signal<number | string | undefined>(undefined);
  public pageSplitterMax = signal<number | string | undefined>(undefined);
  public pageSplitterResizable = signal<boolean>(true);
  public pageSplitterLayout = signal<'horizontal' | 'vertical'>('horizontal');

  /** * Computed: Resolves the exact drawer width to prevent template structural re-renders.
   * Maps NG-Zorro's native size strings to their pixel equivalents if no custom width is provided.
   * 'large' = 736px, 'default' = 378px.
   */
  public effectiveDrawerWidth = computed(() => {
    const w = this.pageDrawerWidth();
    if (w !== undefined && w !== null && w !== '') return w;
    return this.pageDrawerSize() === 'large' ? 736 : 378;
  });

  /** Computed title for the sandbox preview header. */
  public displayTitle = computed(() => {
    const name = this.appName() || 'App';

    // Extract filename from URL
    const url = this.htmlUrl();
    const parts = url.split('/');
    const fName = parts[parts.length - 1] || 'pageTemplate.html';

    return `${name} / ${fName}`;
  });

  /**
   * @constructor
   * @description Sets up reactive effects to natively monitor Angular Signal input changes.
   * This ensures that when the Angular Router reuses the parent dynamic-app-viewer component
   * during a navigation event, the new `definitionId` automatically triggers a fresh API fetch.
   */
  private httpClient = inject(HttpClient);

  constructor() {
    effect(() => {
      const url = this.htmlUrl();
      const lCode = this.liveCode();
      const providedModels = this.dataModels();

      untracked(() => {
        // Reset structural UI overlay states and tracked page entities on navigation.
        this.usedEntitiesOnPage.clear();
        this.isPageModalVisible.set(false);
        this.isPageDrawerVisible.set(false);
        this.isPageSplitterVisible.set(false);

        this.appDataModels.set(providedModels || []);

        if (url) {
          if (lCode === undefined) {
            this.fetchContent();
          } else {
            this.isLoading.set(false);
          }
        }
      });
    });

    const pendingMutations: ModelMutationDescriptor[] = [];
    this.actions$
      .pipe(
        ofType(ProjectDataActions.notifyModelMutations),
        tap(({ mutations }) => {
          if (Array.isArray(mutations)) {
            pendingMutations.push(...mutations);
          }
        }),
        debounceTime(250),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const batch = pendingMutations.splice(0, pendingMutations.length);
        if (batch.length > 0) {
          this.handleAiModelMutations(batch);
        }
      });
  }

  private handleAiModelMutations(mutations: ModelMutationDescriptor[]): void {
    if (
      this.testMode() ||
      this.enableEditorSdk() ||
      this.liveCode() !== undefined ||
      this.isLoading() ||
      !this.previewComponent
    ) {
      return;
    }

    const pageEntities = new Set<string>(this.usedEntitiesOnPage);
    for (const staticEntity of extractRapiderApiEntitiesFromHtml(
      this.htmlContent(),
    )) {
      pageEntities.add(staticEntity);
    }
    if (pageEntities.size === 0) return;

    const allModels = [
      ...(this.appDataModels() || []),
      ...(this.workspaceModels() || []),
    ];
    const matchedTokens = matchPageEntitiesWithMutations(
      pageEntities,
      mutations,
      allModels,
    );
    if (matchedTokens.length === 0) return;

    clearSwrSessionCacheForEntities(matchedTokens);
    this.previewComponent.reloadSandbox(matchedTokens);
  }

  /**
   * @method ngOnInit
   * @description Standard Angular lifecycle hook. Logic moved to the constructor's reactive `effect()`
   * block to support dynamic input changes during route navigation.
   */
  ngOnInit(): void {
    // Logic migrated to reactive effect.
  }

  private fetchContent(): void {
    const url = this.htmlUrl();
    if (!url) return;

    this.contentSub?.unsubscribe();
    this.error.set(null);

    if (!isTrustedAppResourceUrl(url)) {
      console.error('[HtmlUrlPreview] Blocked untrusted page HTML URL:', url);
      this.error.set('Blocked untrusted application page URL.');
      this.isLoading.set(false);
      return;
    }

    const canUseMemoryCache = !/[?&]t=/.test(url);
    if (canUseMemoryCache && HtmlUrlPreviewComponent.htmlCache.has(url)) {
      this.htmlContent.set(HtmlUrlPreviewComponent.htmlCache.get(url) || '');
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);

    this.contentSub = this.httpClient
      .get(url, { responseType: 'text' })
      .subscribe({
        next: (content) => {
          const safeContent = content || '';
          if (canUseMemoryCache && safeContent) {
            if (HtmlUrlPreviewComponent.htmlCache.size >= 100) {
              const oldestKey = HtmlUrlPreviewComponent.htmlCache
                .keys()
                .next().value;
              if (oldestKey) {
                HtmlUrlPreviewComponent.htmlCache.delete(oldestKey);
              }
            }
            HtmlUrlPreviewComponent.htmlCache.set(url, safeContent);
          }
          this.htmlContent.set(safeContent);
          this.isLoading.set(false);
        },
        error: (err) => {
          console.error(
            '[HtmlUrlPreview] Failed to load HTML file from CDN',
            err,
          );
          this.error.set(
            $localize`:@@htmlDefPreview.loadError:Failed to load the requested preview code.`,
          );
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Handler for messages posted outbound from the sandboxed iframe.
   * Can be used in the future to trigger API requests and post results back to the iframe.
   * @param {MessageEvent} event - The message payload from the iframe.
   */
  public handleIframeMessage(event: MessageEvent, targetPreview?: HtmlPreviewComponent): void {
    const data = event.data;
    if (!data) return;

    // 1. Cross-Iframe Broadcast Broker
    // Because every HtmlPreviewComponent listens to the parent window's messages,
    // this broadcast sent by one iframe will be caught by all Angular wrappers.
    // We simply forward it back down into this specific wrapper's iframe.
    if (data.type === 'RAPIDER_BROADCAST') {
      // Emit the outbound broadcast event for the debugger
      this.onApiEvent.emit({
        type: 'broadcast_out',
        eventName: data.eventName,
        payload: data.payload,
        timestamp: new Date(),
      });

      // Emit the inbound broadcast event for the debugger (as it loops back down)
      this.onApiEvent.emit({
        type: 'broadcast_in',
        eventName: data.eventName,
        payload: data.payload,
        timestamp: new Date(),
      });

      const comp = targetPreview || this.previewComponent;
      if (comp) {
        comp.postMessageToIframe({
          type: 'RAPIDER_BROADCAST_RECEIVE',
          eventName: data.eventName,
          payload: data.payload,
        });
      }
      return;
    }

    // 2. Intercept API Requests triggered by the injected window.rapiderApi SDK
    if (data.type === 'RAPIDER_API_REQUEST') {
      const { reqId, operation, entityName, payload } = data;

      const comp = targetPreview || this.previewComponent;
      if (!operation || !entityName) {
        if (comp) {
          comp.postMessageToIframe({
            type: 'RAPIDER_API_ERROR',
            reqId,
            error: 'Operation and entityName are required.',
          });
        }
        return;
      }

      // Enforce manifest entity allowlist so microapps can only query/mutate their own declared dataModels
      const isWorkflowOp =
        operation === 'triggerWorkflow' || operation === 'startWorkflow';
      if (
        !isWorkflowOp &&
        !isAllowedManifestEntity(entityName, this.appDataModels())
      ) {
        const denyMsg = `Access denied: entity '${entityName}' is not declared in this application's manifest.`;
        this.onApiEvent.emit({
          type: 'error',
          reqId,
          operation,
          entityName,
          error: denyMsg,
          timestamp: new Date(),
        });
        if (comp) {
          comp.postMessageToIframe({
            type: 'RAPIDER_API_ERROR',
            reqId,
            error: denyMsg,
          });
        }
        return;
      }

      if (!isWorkflowOp) {
        this.usedEntitiesOnPage.add(String(entityName));
      }

      // Emit the outgoing request for debugging
      this.onApiEvent.emit({
        type: 'request',
        reqId,
        operation,
        entityName,
        payload,
        timestamp: new Date(),
      });

      // E2E Test Mode Mutation Interception
      const isMutation = ['create', 'update', 'updateById', 'delete', 'deleteById'].includes(operation);
      if (this.testMode() && isMutation) {
        const mockId = payload.id || ('test_' + Math.random().toString(36).substr(2, 9));
        const mockResponse = (operation === 'delete' || operation === 'deleteById')
          ? { count: 1, success: true, id: payload.id }
          : {
              id: mockId,
              ...(payload.body || {}),
              createdDate: new Date().toISOString(),
              updatedDate: new Date().toISOString(),
            };

        this.onApiEvent.emit({
          type: 'success',
          reqId,
          operation,
          entityName,
          response: mockResponse,
          timestamp: new Date(),
        });

        const compRes = targetPreview || this.previewComponent;
        if (compRes) {
          compRes.postMessageToIframe({
            type: 'RAPIDER_API_RESPONSE',
            reqId,
            payload: mockResponse,
          });
        }
        return;
      }

      let pathBase = '';
      if (this.authStore.useProjectApiOnDataManagerCalls()) {
        // Format EntityName to standard REST path (e.g. 'ProjectModel' -> 'project-models')
        const kebabName = entityName
          .replace(/([a-z])([A-Z])/g, '$1-$2')
          .replace(/[\s_]+/g, '-')
          .toLowerCase();
        pathBase = kebabName.endsWith('s') ? kebabName : kebabName + 's';
      } else {
        // Use the new dynamic Project Data Manager API
        const models = this.appDataModels();
        const lowerEntity = entityName.toLowerCase();
        const matchedModel = models.find(
          (m: any) =>
            m.dataTableName === entityName ||
            m.datatableName === entityName ||
            m.name === entityName ||
            (m.dataTableName && m.dataTableName.toLowerCase() === lowerEntity) ||
            (m.datatableName && m.datatableName.toLowerCase() === lowerEntity) ||
            (m.name && m.name.toLowerCase() === lowerEntity),
        );
        const tableName =
          matchedModel?.dataTableName ||
          matchedModel?.datatableName ||
          matchedModel?.name ||
          entityName;
        pathBase = `project-data-manager/${tableName}`;
      }

      let method = 'GET';
      let path = `/${pathBase}`;
      let body = null;

      // Map LoopBack repository operations to RESTful endpoints
      switch (operation) {
        case 'create':
          method = 'POST';
          body = payload.body;
          break;
        case 'find':
          method = 'GET';
          if (payload.filter)
            path += `?filter=${encodeURIComponent(JSON.stringify(payload.filter))}`;
          break;
        case 'findById':
          method = 'GET';
          path += `/${payload.id}`;
          if (payload.filter)
            path += `?filter=${encodeURIComponent(JSON.stringify(payload.filter))}`;
          break;
        case 'update':
        case 'updateById':
          method = 'PATCH';
          path += `/${payload.id}`;
          body = payload.body;
          break;
        case 'deleteById':
          method = 'DELETE';
          path += `/${payload.id}`;
          break;
        case 'count':
          method = 'GET';
          path += `/count`;
          if (payload.where)
            path += `?where=${encodeURIComponent(JSON.stringify(payload.where))}`;
          break;
        case 'triggerWorkflow':
        case 'startWorkflow':
          method = 'POST';
          path = '/workflow-management/workflow-events/run-with-jwt';
          body = {
            name: payload.name || entityName,
            payload: payload.payload || {},
            triggerSource: 'RapiderApi',
          };
          break;
        default:
          const compErr = targetPreview || this.previewComponent;
          if (compErr) {
            compErr.postMessageToIframe({
              type: 'RAPIDER_API_ERROR',
              reqId,
              error: `Unknown operation: ${operation}`,
            });
          }
          return;
      }

      const observerObj = {
        next: (response: any) => {
          // Emit success response for debugging
          this.onApiEvent.emit({
            type: 'success',
            reqId,
            operation,
            entityName,
            response,
            timestamp: new Date(),
          });

          const compRes = targetPreview || this.previewComponent;
          if (compRes) {
            compRes.postMessageToIframe({
              type: 'RAPIDER_API_RESPONSE',
              reqId,
              payload: response,
            });
          }
        },
        error: (err: any) => {
          console.error('[HtmlUrlPreview] Sandbox API Error:', err);

          this.onApiEvent.emit({
            type: 'error',
            reqId,
            operation,
            entityName,
            error:
              err?.error?.error?.message ||
              err?.error?.message ||
              err.message ||
              err.statusText ||
              'Unknown API Error',
            timestamp: new Date(),
          });

          const compFail = targetPreview || this.previewComponent;
          if (compFail) {
            compFail.postMessageToIframe({
              type: 'RAPIDER_API_ERROR',
              reqId,
              error:
                err?.error?.error?.message ||
                err?.error?.message ||
                err.message ||
                err.statusText ||
                'Unknown API Error',
            });
          }
        },
      };

      // Execute the dynamic API call securely
      this.projectDataService
        .executeProjectApiCall(method, path, body)
        .subscribe(observerObj);
    }
    // 3. Intercept Editor SDK events
    else if (data.type === 'EDITOR_CLICK' || data.type === 'EDITOR_HOVER') {
      this.onEditorEvent.emit(data);
    }
    // 4. Intercept UI Actions (Navigate, Notifications, Modals, Drawers, Splitters)
    else if (data.type === 'RAPIDER_UI_ACTION') {
      const action = data.action;
      if (!action) return;

      const payload = action.payload || {};

      if (action.type === 'navigate' && payload.route) {
        if (typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive) {
          return;
        }
        if (this.isPageDrawerVisible()) {
          this.closePageDrawer();
        }
        if (this.isPageModalVisible()) {
          this.closePageModal();
        }
        let targetSegments: string[] = [];
        if (typeof payload.route === 'string') {
          targetSegments = (payload.route as string).split('/').filter(Boolean);
        } else if (Array.isArray(payload.route)) {
          targetSegments = [...payload.route].filter(Boolean);
        }

        const currentSegments = this.router.url
          .split('?')[0]
          .split('/')
          .filter(Boolean);

        let finalRoute = [];
        if (currentSegments[0] === 'app' && currentSegments.length >= 2) {
          finalRoute = ['/app', currentSegments[1], ...targetSegments];
        } else if (
          currentSegments[0] === 'app-builder' &&
          currentSegments.length >= 3 &&
          currentSegments[2] === 'preview'
        ) {
          finalRoute = [
            '/app-builder',
            currentSegments[1],
            'preview',
            ...targetSegments,
          ];
        } else {
          finalRoute = ['/', ...targetSegments];
        }

        const navExtras: any = {};
        if (payload.queryParams) {
          navExtras.queryParams = payload.queryParams;
        }

        this.router.navigate(finalRoute, navExtras);
      } else if (action.type === 'showNotification' && payload.notification) {
        this.notificationService.createNotification(
          payload.notification.type || 'info',
          'System Message',
          payload.notification.message,
        );
      } else if (action.type === 'showPageModal' && payload.pageModal) {
        const pm = payload.pageModal;
        this.openPageModal(pm.route, pm.payload, pm.title, pm.width);
      } else if (action.type === 'closePageModal') {
        this.closePageModal();
      } else if (action.type === 'showPageDrawer' && payload.pageDrawer) {
        const pd = payload.pageDrawer;
        this.openPageDrawer(
          pd.route,
          pd.payload,
          pd.title,
          pd.placement,
          pd.size,
          pd.width,
        );
      } else if (action.type === 'closePageDrawer') {
        this.closePageDrawer();
      } else if (action.type === 'showPageSplitter' && payload.pageSplitter) {
        const ps = payload.pageSplitter;
        this.openPageSplitter(
          ps.route,
          ps.payload,
          ps.title,
          ps.icon,
          ps.defaultSize,
          ps.min,
          ps.max,
          ps.resizable,
          ps.layout,
        );
      }
    } else {
      // Ignore other window messages (like Webpack HMR or devtools)
      if (data.type !== 'TOGGLE_DARK_MODE') {
        // Silently drop
      }
    }
  }

  // --- OVERLAY & NAVIGATION HANDLERS ---

  /**
   * Opens an inner page modal, dynamically resolving the requested page definition by its route.
   */
  private openPageModal(
    routeKey: string,
    payload: any,
    title?: string,
    width?: number | string,
  ): void {
    this.pageModalTitle.set(title || 'Page Modal');
    this.pageModalWidth.set(width || 800);
    this.pageModalPayload.set(payload || {});

    // Find page in pagesManifest
    const manifest = this.pagesManifest();
    const targetPage = manifest.find(
      (p) => p.route === routeKey || p.name === routeKey,
    );

    if (!targetPage) {
      this.notificationService.createNotification(
        'error',
        'Error',
        `Could not find a page definition matching route: ${routeKey}`,
      );
      this.isPageModalLoading.set(false);
      return;
    }

    let resolvedType = targetPage.type;
    if (!resolvedType) {
      resolvedType = targetPage.htmlUrl ? 'AppPage' : 'AppDynamicPage';
    }

    if (targetPage.htmlUrl && !isTrustedAppResourceUrl(targetPage.htmlUrl)) {
      this.notificationService.createNotification(
        'error',
        'Security Error',
        'Blocked untrusted modal page URL.',
      );
      this.isPageModalLoading.set(false);
      return;
    }

    const pageId = targetPage.id || routeKey;
    this.pageModalId.set(pageId);
    this.pageModalType.set(resolvedType);
    this.pageModalUrl.set(targetPage.htmlUrl || null);

    if (resolvedType === 'AppPage') {
      setTimeout(() => {
        this.isPageModalVisible.set(true);
        this.isPageModalLoading.set(false);
      }, 0);
      return;
    }

    if (targetPage.template) {
      const pageConfig: PageConfig = {
        pageId: pageId,
        pageName: targetPage.name,
        route: targetPage.route,
        dataVariables: targetPage.dataVariables || [],
        dataTransformations: targetPage.dataTransformations || [],
        events: targetPage.events || [],
        template: targetPage.template,
      };
      this.pageModalConfig.set(pageConfig);
      setTimeout(() => {
        this.isPageModalVisible.set(true);
        this.isPageModalLoading.set(false);
      }, 0);
      return;
    }

    if (!targetPage.htmlUrl) {
      if (resolvedType === 'AppDynamicPage') {
        this.definitionController
          .readFileVersion({
            id: pageId,
            name: 'pageTemplate',
            extension: 'html',
          })
          .subscribe({
            next: (templateHtml: string) => {
              const pageConfig: PageConfig = {
                pageId: pageId,
                pageName: targetPage.name,
                route: targetPage.route,
                dataVariables: targetPage.dataVariables || [],
                dataTransformations: targetPage.dataTransformations || [],
                events: targetPage.events || [],
                template: templateHtml || '',
              };
              this.pageModalConfig.set(pageConfig);
              setTimeout(() => {
                this.isPageModalVisible.set(true);
                this.isPageModalLoading.set(false);
              }, 0);
            },
            error: (err) => {
              console.error('Error in readFileVersion:', err);
              this.notificationService.createNotification(
                'error',
                'Error',
                `Failed to load the HTML template for modal page (ID: ${pageId}).`,
              );
              this.isPageModalLoading.set(false);
            },
          });
        return;
      }

      this.notificationService.createNotification(
        'error',
        'Error',
        'No htmlUrl or template provided for this page in manifest.',
      );
      this.isPageModalLoading.set(false);
      return;
    }

    this.httpClient
      .get(targetPage.htmlUrl, { responseType: 'text' })
      .subscribe({
        next: (templateHtml: string) => {
          const pageConfig: PageConfig = {
            pageId: pageId,
            pageName: targetPage.name,
            route: targetPage.route,
            dataVariables: targetPage.dataVariables || [],
            dataTransformations: targetPage.dataTransformations || [],
            events: targetPage.events || [],
            template: templateHtml || '',
          };
          this.pageModalConfig.set(pageConfig);
          setTimeout(() => {
            this.isPageModalVisible.set(true);
            this.isPageModalLoading.set(false);
          }, 0);
        },
        error: () => {
          this.notificationService.createNotification(
            'error',
            'Error',
            'Failed to load the HTML template for the modal page from CDN.',
          );
          this.isPageModalLoading.set(false);
        },
      });
  }

  public closePageModal(): void {
    this.isPageModalVisible.set(false);
    this.pageModalConfig.set(null);
  }

  /**
   * Opens an inner page drawer, dynamically resolving the requested page definition by its route.
   */
  private openPageDrawer(
    routeKey: string,
    payload: any,
    title?: string,
    placement?: 'left' | 'right' | 'top' | 'bottom',
    size?: 'default' | 'large',
    width?: number | string,
  ): void {
    this.pageDrawerTitle.set(title || 'Drawer View');
    this.pageDrawerPlacement.set(placement || 'right');
    this.pageDrawerSize.set(size || 'large');
    this.pageDrawerWidth.set(width);
    this.pageDrawerPayload.set(payload || {});

    // Find page in pagesManifest
    const manifest = this.pagesManifest();
    const targetPage = manifest.find(
      (p) => p.route === routeKey || p.name === routeKey,
    );

    if (!targetPage) {
      this.notificationService.createNotification(
        'error',
        'Error',
        `Could not find a page definition matching route: ${routeKey}`,
      );
      this.isPageDrawerLoading.set(false);
      return;
    }

    let resolvedType = targetPage.type;
    if (!resolvedType) {
      resolvedType = targetPage.htmlUrl ? 'AppPage' : 'AppDynamicPage';
    }

    if (targetPage.htmlUrl && !isTrustedAppResourceUrl(targetPage.htmlUrl)) {
      this.notificationService.createNotification(
        'error',
        'Security Error',
        'Blocked untrusted drawer page URL.',
      );
      this.isPageDrawerLoading.set(false);
      return;
    }

    const pageId = targetPage.id || routeKey;
    this.pageDrawerId.set(pageId);
    this.pageDrawerType.set(resolvedType);
    this.pageDrawerUrl.set(targetPage.htmlUrl || null);

    if (resolvedType === 'AppPage') {
      setTimeout(() => {
        this.isPageDrawerVisible.set(true);
        this.isPageDrawerLoading.set(false);
      }, 0);
      return;
    }

    if (targetPage.template) {
      const pageConfig: PageConfig = {
        pageId: pageId,
        pageName: targetPage.name,
        route: targetPage.route,
        dataVariables: targetPage.dataVariables || [],
        dataTransformations: targetPage.dataTransformations || [],
        events: targetPage.events || [],
        template: targetPage.template,
      };
      this.pageDrawerConfig.set(pageConfig);
      setTimeout(() => {
        this.isPageDrawerVisible.set(true);
        this.isPageDrawerLoading.set(false);
      }, 0);
      return;
    }

    if (!targetPage.htmlUrl) {
      if (resolvedType === 'AppDynamicPage') {
        this.definitionController
          .readFileVersion({
            id: pageId,
            name: 'pageTemplate',
            extension: 'html',
          })
          .subscribe({
            next: (templateHtml: string) => {
              const pageConfig: PageConfig = {
                pageId: pageId,
                pageName: targetPage.name,
                route: targetPage.route,
                dataVariables: targetPage.dataVariables || [],
                dataTransformations: targetPage.dataTransformations || [],
                events: targetPage.events || [],
                template: templateHtml || '',
              };
              this.pageDrawerConfig.set(pageConfig);
              setTimeout(() => {
                this.isPageDrawerVisible.set(true);
                this.isPageDrawerLoading.set(false);
              }, 0);
            },
            error: (err) => {
              console.error('Error in readFileVersion:', err);
              this.notificationService.createNotification(
                'error',
                'Error',
                `Failed to load the HTML template for drawer page (ID: ${pageId}).`,
              );
              this.isPageDrawerLoading.set(false);
            },
          });
        return;
      }

      this.notificationService.createNotification(
        'error',
        'Error',
        'No htmlUrl or template provided for this page in manifest.',
      );
      this.isPageDrawerLoading.set(false);
      return;
    }

    this.httpClient
      .get(targetPage.htmlUrl, { responseType: 'text' })
      .subscribe({
        next: (templateHtml: string) => {
          const pageConfig: PageConfig = {
            pageId: pageId,
            pageName: targetPage.name,
            route: targetPage.route,
            dataVariables: targetPage.dataVariables || [],
            dataTransformations: targetPage.dataTransformations || [],
            events: targetPage.events || [],
            template: templateHtml || '',
          };
          this.pageDrawerConfig.set(pageConfig);
          setTimeout(() => {
            this.isPageDrawerVisible.set(true);
            this.isPageDrawerLoading.set(false);
          }, 0);
        },
        error: () => {
          this.notificationService.createNotification(
            'error',
            'Error',
            'Failed to load the HTML template for the drawer page from CDN.',
          );
          this.isPageDrawerLoading.set(false);
        },
      });
  }

  public closePageDrawer(): void {
    this.isPageDrawerVisible.set(false);
    this.pageDrawerConfig.set(null);
  }

  /**
   * Transforms the view into an NZ-Splitter to project a secondary page view side-by-side.
   */
  private openPageSplitter(
    routeKey: string,
    payload: any,
    title?: string,
    icon?: string,
    defaultSize?: number | string,
    min?: number | string,
    max?: number | string,
    resizable?: boolean,
    layout?: 'horizontal' | 'vertical',
  ): void {
    this.pageSplitterTitle.set(title);
    this.pageSplitterIcon.set(icon);
    this.pageSplitterDefaultSize.set(defaultSize ?? '30%');

    this.pageSplitterMin.set(
      min !== undefined && min !== null ? min : undefined,
    );
    this.pageSplitterMax.set(
      max !== undefined && max !== null ? max : undefined,
    );

    this.pageSplitterResizable.set(resizable ?? true);
    this.pageSplitterLayout.set(layout || 'horizontal');
    this.pageSplitterPayload.set(payload || {});

    this.isPageSplitterVisible.set(true);
    this.isPageSplitterLoading.set(true);

    // Find page in pagesManifest
    const manifest = this.pagesManifest();
    const targetPage = manifest.find(
      (p) => p.route === routeKey || p.name === routeKey,
    );

    if (!targetPage) {
      this.notificationService.createNotification(
        'error',
        'Error',
        `Could not find a page definition matching route: ${routeKey}`,
      );
      this.isPageSplitterLoading.set(false);
      return;
    }

    if (targetPage.htmlUrl && !isTrustedAppResourceUrl(targetPage.htmlUrl)) {
      this.notificationService.createNotification(
        'error',
        'Security Error',
        'Blocked untrusted splitter page URL.',
      );
      this.isPageSplitterLoading.set(false);
      return;
    }

    const pageId = targetPage.id || routeKey;
    this.pageSplitterId.set(pageId);
    this.pageSplitterType.set(targetPage.type || 'AppPage');
    this.pageSplitterUrl.set(targetPage.htmlUrl || null);

    if (targetPage.type === 'AppPage') {
      this.isPageSplitterLoading.set(false);
      return;
    }

    if (targetPage.template) {
      const pageConfig: PageConfig = {
        pageId: pageId,
        pageName: targetPage.name,
        route: targetPage.route,
        dataVariables: targetPage.dataVariables || [],
        dataTransformations: targetPage.dataTransformations || [],
        events: targetPage.events || [],
        template: targetPage.template,
      };
      this.pageSplitterConfig.set(pageConfig);
      this.isPageSplitterLoading.set(false);
      return;
    }

    if (!targetPage.htmlUrl) {
      this.notificationService.createNotification(
        'error',
        'Error',
        'No htmlUrl or template provided for this page in manifest.',
      );
      this.isPageSplitterLoading.set(false);
      return;
    }

    this.httpClient
      .get(targetPage.htmlUrl, { responseType: 'text' })
      .subscribe({
        next: (templateHtml: string) => {
          const pageConfig: PageConfig = {
            pageId: pageId,
            pageName: targetPage.name,
            route: targetPage.route,
            dataVariables: targetPage.dataVariables || [],
            dataTransformations: targetPage.dataTransformations || [],
            events: targetPage.events || [],
            template: templateHtml || '',
          };
          this.pageSplitterConfig.set(pageConfig);
          this.isPageSplitterLoading.set(false);
        },
        error: () => {
          this.notificationService.createNotification(
            'error',
            'Error',
            'Failed to load the HTML template for the splitter page from CDN.',
          );
          this.isPageSplitterLoading.set(false);
        },
      });
  }

  public closePageSplitter(): void {
    this.isPageSplitterVisible.set(false);
    this.pageSplitterConfig.set(null);
  }
}
